/**
 * Build recursive modification trees from flat parent_modification_id / temporary_id rows.
 * Prefer in-memory trees over recursive SQL (IONOS-safe).
 */

export const MAX_MODIFICATION_DEPTH = 8;

export interface HierarchyModLike {
  id?: string | number;
  temporary_id?: string | null;
  parent_temporary_id?: string | null;
  parent_modification_id?: string | number | null;
  parentComponent?: string | null;
  category?: string | null;
  title?: string;
  sort_order?: number | null;
}

export interface HierarchyNode<T extends HierarchyModLike> {
  mod: T;
  index: number;
  depth: number;
  children: HierarchyNode<T>[];
}

function nodeKey(mod: HierarchyModLike, index: number): string {
  if (mod.temporary_id) return `t:${mod.temporary_id}`;
  if (mod.id !== undefined && mod.id !== null && `${mod.id}` !== '') return `id:${mod.id}`;
  return `i:${index}`;
}

function parentKey(mod: HierarchyModLike): string | null {
  if (mod.parent_temporary_id) return `t:${mod.parent_temporary_id}`;
  if (mod.parent_modification_id !== undefined && mod.parent_modification_id !== null && `${mod.parent_modification_id}` !== '') {
    return `id:${mod.parent_modification_id}`;
  }
  return null;
}

/** Build a forest of trees preserving source order. */
export function buildModificationForest<T extends HierarchyModLike>(mods: T[]): HierarchyNode<T>[] {
  const nodes: HierarchyNode<T>[] = mods.map((mod, index) => ({
    mod,
    index,
    depth: 0,
    children: [],
  }));

  const byKey = new Map<string, HierarchyNode<T>>();
  nodes.forEach((node) => {
    byKey.set(nodeKey(node.mod, node.index), node);
  });

  const roots: HierarchyNode<T>[] = [];

  nodes.forEach((node) => {
    const pKey = parentKey(node.mod);
    if (pKey && byKey.has(pKey) && pKey !== nodeKey(node.mod, node.index)) {
      const parent = byKey.get(pKey)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const bySort = (a: HierarchyNode<T>, b: HierarchyNode<T>) => {
    const sa = a.mod.sort_order ?? 0;
    const sb = b.mod.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    return a.index - b.index;
  };
  const sortTree = (node: HierarchyNode<T>) => {
    node.children.sort(bySort);
    node.children.forEach(sortTree);
  };
  roots.sort(bySort);
  roots.forEach(sortTree);

  const assignDepth = (node: HierarchyNode<T>, depth: number, guard: number) => {
    node.depth = depth;
    if (guard > MAX_MODIFICATION_DEPTH + 2) return;
    node.children.forEach((child) => assignDepth(child, depth + 1, guard + 1));
  };
  roots.forEach((r) => assignDepth(r, 0, 0));

  return roots;
}

/** Group roots by category, preserving category and within-category order. */
export function buildCategoryForest<T extends HierarchyModLike>(
  mods: T[]
): Array<{ category: string; roots: HierarchyNode<T>[]; itemCount: number }> {
  const forest = buildModificationForest(mods);
  const categoryOrder: string[] = [];
  const map = new Map<string, HierarchyNode<T>[]>();

  const countNodes = (node: HierarchyNode<T>): number =>
    1 + node.children.reduce((sum, c) => sum + countNodes(c), 0);

  forest.forEach((root) => {
    const category = (root.mod.category || 'Miscellaneous').trim() || 'Miscellaneous';
    if (!map.has(category)) {
      map.set(category, []);
      categoryOrder.push(category);
    }
    map.get(category)!.push(root);
  });

  return categoryOrder.map((category) => {
    const roots = map.get(category) || [];
    const itemCount = roots.reduce((sum, r) => sum + countNodes(r), 0);
    return { category, roots, itemCount };
  });
}

export function countChildren<T extends HierarchyModLike>(node: HierarchyNode<T>): number {
  return node.children.reduce((sum, c) => sum + 1 + countChildren(c), 0);
}

/** Breadcrumb labels from root to node (exclusive of current optional). */
export function hierarchyBreadcrumb<T extends HierarchyModLike>(
  mods: T[],
  index: number,
  maxSegments = 4
): string[] {
  const byTemp = new Map<string, number>();
  const byId = new Map<string, number>();
  mods.forEach((m, i) => {
    if (m.temporary_id) byTemp.set(m.temporary_id, i);
    if (m.id !== undefined && m.id !== null) byId.set(String(m.id), i);
  });

  const titles: string[] = [];
  let cursor = index;
  let guard = 0;
  while (guard++ < MAX_MODIFICATION_DEPTH + 2) {
    const mod = mods[cursor];
    if (!mod) break;
    const pTemp = mod.parent_temporary_id;
    const pId = mod.parent_modification_id;
    let parentIndex: number | undefined;
    if (pTemp && byTemp.has(pTemp)) parentIndex = byTemp.get(pTemp);
    else if (pId !== undefined && pId !== null && byId.has(String(pId))) parentIndex = byId.get(String(pId));
    if (parentIndex === undefined) break;
    const parentTitle = (mods[parentIndex]?.title || '').trim();
    if (parentTitle) titles.unshift(parentTitle);
    cursor = parentIndex;
  }

  if (titles.length > maxSegments) {
    return ['…', ...titles.slice(-maxSegments + 1)];
  }
  return titles;
}

function parentIndexOf(
  mods: HierarchyModLike[],
  index: number,
  byTemp: Map<string, number>,
  byId: Map<string, number>
): number | null {
  if (index < 0 || index >= mods.length) return null;
  const mod: HierarchyModLike = mods[index] as HierarchyModLike;
  const pTemp = mod.parent_temporary_id ?? null;
  if (pTemp && byTemp.has(pTemp)) {
    return byTemp.get(pTemp) ?? null;
  }
  const pId = mod.parent_modification_id;
  if (pId !== undefined && pId !== null && String(pId) !== '') {
    return byId.get(String(pId)) ?? null;
  }
  return null;
}

export function wouldCreateHierarchyCycle(
  mods: HierarchyModLike[],
  childIndex: number,
  newParentIndex: number | null
): boolean {
  if (newParentIndex === null) return false;
  if (childIndex === newParentIndex) return true;

  const byTemp = new Map<string, number>();
  const byId = new Map<string, number>();
  for (let i = 0; i < mods.length; i += 1) {
    const m = mods[i];
    if (m.temporary_id) byTemp.set(m.temporary_id, i);
    if (m.id !== undefined && m.id !== null) byId.set(String(m.id), i);
  }

  let cursor: number | null = newParentIndex;
  for (let guard = 0; cursor !== null && guard < MAX_MODIFICATION_DEPTH + 4; guard += 1) {
    if (cursor === childIndex) return true;
    cursor = parentIndexOf(mods, cursor, byTemp, byId);
  }
  return false;
}
